/* ============================================
   PART FINDER — CFMoto | Everest Motorcycles
   ============================================ */

(function () {
  'use strict';

  const DATA = window.PART_FINDER_DATA || { brands: [] };

  // ── Per-brand page config (set via data-pf-* on .pf-wrap) ────────────────
  const PF_WRAP = document.querySelector('.pf-wrap');
  const CONFIG = {
    brand:     PF_WRAP?.dataset.pfBrand || null,
    // ccBuckets: comma-separated list of buckets to show; empty string = no filter bar
    ccBuckets: PF_WRAP?.hasAttribute('data-pf-cc-buckets')
      ? (PF_WRAP.dataset.pfCcBuckets || '').split(',').filter(Boolean)
      : ['ALL','150-299','300-599','600-799','800-1000'],
  };

  // ── Engine CC helpers ─────────────────────
  function getEngineCC(name) {
    const n = parseInt((name.match(/\d+/) || ['0'])[0]);
    return n;
  }
  function getEngineBucket(name) {
    const cc = getEngineCC(name);
    if (cc === 0 || cc < 300) return '150-299';   // 150, 250, XO PAPIO (125)
    if (cc < 600)             return '300-599';   // 300, 400, 450, 500
    if (cc < 800)             return '600-799';   // 650, 675, 700
    return '800-1000';                             // 800+
  }

  // ── State ─────────────────────────────────
  const state = {
    view: 'brands',
    brandId: null,
    modelName: null,
    modelId: null,
    diagramCode: null,
    sectionFilter: 'ALL',
    ccFilter: 'ALL',
    searchQuery: '',
  };

  // ── DOM shortcuts ─────────────────────────
  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => [...(ctx || document).querySelectorAll(sel)];

  const views = {
    brands:   $('#pf-view-brands'),
    models:   $('#pf-view-models'),
    years:    $('#pf-view-years'),
    diagrams: $('#pf-view-diagrams'),
    detail:   $('#pf-view-detail'),
  };

  // ── Helpers ───────────────────────────────
  let pfZoomState = 1;
  let panX = 0, panY = 0;
  let isPanning = false, panStartX = 0, panStartY = 0;
  let _isRestoringHistory = false;   // true while handling browser back/forward

  // Live availability cache: variantId (number) → { available: bool, price: number (cents) }
  let _liveAvail = {};

  function fmtPrice(n) { return '$' + Number(n).toFixed(2); }

  function isInStock(part) {
    // Primary: pre-generated stock map (from Admin API inventory, uploaded daily)
    if (window._pfOOS && part.variantId) {
      return !window._pfOOS.has(part.variantId);
    }
    // Fallback: static SOH from data.js
    return part.status !== 'NON-STOCKED' && part.soh > 0;
  }

  function getLivePrice(part) {
    if (part.variantId && part.variantId in _liveAvail) {
      const p = _liveAvail[part.variantId].price;
      return p > 0 ? p / 100 : null;
    }
    return null;
  }

  function getBrand()   { return DATA.brands.find(b => b.id === state.brandId); }
  function getModel()   { const b = getBrand(); return b && b.models.find(m => m.id === state.modelId); }
  function getDiagram() { const m = getModel(); return m && m.diagrams.find(d => d.code === state.diagramCode); }
  function getModelsForName() {
    const b = getBrand();
    if (!b || !state.modelName) return [];
    return b.models.filter(m => m.name === state.modelName);
  }

  // ── Views ─────────────────────────────────
  function showView(name) {
    Object.values(views).forEach(v => v && v.classList.remove('active'));
    if (views[name]) views[name].classList.add('active');
    state.view = name;
    renderBreadcrumb();

    const gs = $('#pf-global-search');
    if (gs) gs.classList.toggle('visible', name === 'diagrams');

    window.scrollTo({ top: 0, behavior: 'instant' });

    if (name !== 'detail' && window._pfStickyOff) {
      window._pfStickyOff(); window._pfStickyOff = null;
    }
    if (name === 'detail') requestAnimationFrame(enableStickyPanel);

    // Push a browser history entry so back/forward arrows work within the Part Finder
    if (!_isRestoringHistory) {
      const histState = {
        view:          state.view,
        brandId:       state.brandId,
        modelName:     state.modelName,
        modelId:       state.modelId,
        diagramCode:   state.diagramCode,
        sectionFilter: state.sectionFilter,
        ccFilter:      state.ccFilter,
      };
      // Build a ?pf= query-param URL so each step is shareable / bookmarkable
      history.pushState(histState, '', _buildPfUrl(histState));
    }
  }

  // Build a shareable URL using a single ?pf= query param (avoids hash ambiguity)
  // Format: /pages/part-finder-kymco?pf=ModelName/Year/DiagCode
  function _buildPfUrl(s) {
    const segs = [];
    if (!CONFIG.brand && s.brandId) segs.push(s.brandId);
    if (s.modelName) segs.push(s.modelName);
    if (s.modelId) {
      const brd = DATA.brands.find(b => b.id === s.brandId);
      const m = brd && brd.models.find(m => m.id === s.modelId);
      if (m) segs.push(m.year);
    }
    if (s.diagramCode) segs.push(s.diagramCode);
    if (!segs.length) return location.pathname;
    return location.pathname + '?pf=' + segs.map(encodeURIComponent).join('%2F');
  }

  // Parse ?pf= query param to restore a deep-linked state
  function _parsePfUrl() {
    const raw = new URLSearchParams(location.search).get('pf');
    if (!raw) return null;
    const segs = raw.split('/');
    if (CONFIG.brand) {
      // brand page: ?pf=ModelName/Year/DiagCode
      return { brand: CONFIG.brand, model: segs[0] || null, year: segs[1] || null, diag: segs[2] || null };
    } else {
      // home page: ?pf=brandId/ModelName/Year/DiagCode
      return { brand: segs[0] || null, model: segs[1] || null, year: segs[2] || null, diag: segs[3] || null };
    }
  }

  // Handle browser back / forward arrows
  window.addEventListener('popstate', function (e) {
    const s = e.state;
    // If no pf state (e.g. user went back before the Part Finder) → reset to brands
    if (!s || !s.view) {
      _isRestoringHistory = true;
      state.brandId = null; state.modelName = null; state.modelId = null;
      state.diagramCode = null; state.sectionFilter = 'ALL'; state.ccFilter = 'ALL';
      renderBrands();
      showView('brands');
      _isRestoringHistory = false;
      return;
    }

    _isRestoringHistory = true;
    _liveAvail = {};
    state.brandId       = s.brandId       || null;
    state.modelName     = s.modelName     || null;
    state.modelId       = s.modelId       || null;
    state.diagramCode   = s.diagramCode   || null;
    state.sectionFilter = s.sectionFilter || 'ALL';
    state.ccFilter      = s.ccFilter      || 'ALL';
    state.searchQuery   = '';

    // Re-render the restored view
    switch (s.view) {
      case 'brands':   renderBrands();   break;
      case 'models':   renderModels();   break;
      case 'years':    renderYears();    break;
      case 'diagrams': renderDiagrams(); break;
      case 'detail':
        renderDetail();
        const diag = getDiagram();
        if (diag) fetchLiveAvailability(diag).catch(() => {});
        break;
    }

    // Switch view without pushing to history again
    Object.values(views).forEach(v => v && v.classList.remove('active'));
    if (views[s.view]) views[s.view].classList.add('active');
    state.view = s.view;
    renderBreadcrumb();
    const gs2 = $('#pf-global-search');
    if (gs2) gs2.classList.toggle('visible', s.view === 'diagrams');
    window.scrollTo({ top: 0, behavior: 'instant' });
    if (s.view !== 'detail' && window._pfStickyOff) {
      window._pfStickyOff(); window._pfStickyOff = null;
    }
    if (s.view === 'detail') requestAnimationFrame(enableStickyPanel);

    _isRestoringHistory = false;
  });

  // ── Breadcrumb + header ───────────────────
  function renderBreadcrumb() {
    const bc = $('#pf-breadcrumb');
    if (!bc) return;
    const v = state.view;
    const brand = getBrand(), model = getModel(), diag = getDiagram();

    const PF_HOME = '/pages/part-finder';
    const crumbs = CONFIG.brand
      ? [{ label: 'Part Finder', href: PF_HOME }]
      : [{ label: 'Part Finder', view: 'brands' }];
    // Always show brand name as second crumb
    if (CONFIG.brand && brand) crumbs.push({ label: brand.name, view: 'models' });
    else if (!CONFIG.brand && state.brandId) crumbs.push({ label: brand ? brand.name : state.brandId, view: 'models' });
    if (state.modelName)  crumbs.push({ label: state.modelName, view: 'years' });
    if (state.modelId)    crumbs.push({ label: model ? model.year : state.modelId, view: 'diagrams' });
    if (state.diagramCode) crumbs.push({ label: diag ? `${diag.code} — ${diag.name}` : state.diagramCode, view: 'detail' });

    bc.innerHTML = crumbs.map((c, i) => {
      const last = i === crumbs.length - 1;
      if (last) return `<span style="color:#111;font-weight:600">${c.label}</span>`;
      if (c.href) return `<a href="${c.href}">${c.label}</a><span>›</span>`;
      return `<a onclick="pfNav('${c.view}')">${c.label}</a><span>›</span>`;
    }).join('');

    const titleEl = $('#pf-page-title');
    if (titleEl) {
      if (v === 'brands')   titleEl.textContent = 'Parts Finder';
      else if (v === 'models')   titleEl.textContent = (brand ? brand.name : '') + ' Parts';
      else if (v === 'years')    titleEl.textContent = (state.modelName || '') + ' — Select Year';
      else if (v === 'diagrams') titleEl.textContent = model ? `${model.name} ${model.year}` : 'Diagrams';
      else if (v === 'detail')   titleEl.textContent = diag ? diag.name : '';
    }

    const subtitleEl = $('#pf-page-subtitle');
    if (subtitleEl) {
      const forName = getModelsForName();
      if (v === 'years' && forName.length)
        subtitleEl.textContent = `${forName.length} model year${forName.length > 1 ? 's' : ''} available`;
      else if (v === 'diagrams' && model)
        subtitleEl.textContent = `${model.diagrams.length} exploded parts diagrams`;
      else if (v === 'detail' && diag && model)
        subtitleEl.textContent = `${model.name} ${model.year} · ${diag.code}`;
      else
        subtitleEl.textContent = '';
    }

    const headerRight = $('#pf-header-right');
    if (headerRight) headerRight.style.display = v === 'brands' ? 'none' : 'flex';

    const backDefs = {
      models:   CONFIG.brand
        ? { label: 'All Brands', href: '/pages/part-finder' }
        : { label: 'Back to Brands',   dest: 'brands'   },
      years:    { label: 'Back to Models',    dest: 'models'   },
      diagrams: { label: 'Back to Years',     dest: 'years'    },
      detail:   { label: 'Back to Diagrams',  dest: 'diagrams' },
    };
    const def = backDefs[v];
    const btnBack = $('#pf-btn-back-main');
    if (btnBack && def) {
      const lbl = btnBack.querySelector('.pf-btn-back-label');
      if (lbl) lbl.textContent = def.label;
      if (def.href) btnBack.onclick = () => { window.location.href = def.href; };
      else btnBack.onclick = () => pfNav(def.dest);
    }
  }

  window.pfNav = function (view) {
    if (view === 'brands' && CONFIG.brand) { window.location.href = '/pages/part-finder'; return; }
    if (view === 'brands')   { state.brandId = null; state.modelName = null; state.modelId = null; state.diagramCode = null; state.ccFilter = 'ALL'; }
    if (view === 'models')   { state.modelName = null; state.modelId = null; state.diagramCode = null; state.ccFilter = 'ALL'; }
    if (view === 'years')    { state.modelId = null; state.diagramCode = null; state.sectionFilter = 'ALL'; }
    if (view === 'diagrams') { state.diagramCode = null; state.sectionFilter = 'ALL'; }
    if (['brands', 'models', 'years'].includes(view)) clearGlobalSearch();
    showView(view);
  };

  // ── Global Part Search ────────────────────
  function initGlobalSearch() {
    const input = $('#pf-global-search-input');
    const results = $('#pf-global-search-results');
    if (!input || !results) return;

    input.addEventListener('input', () => {
      const q = input.value.trim().toLowerCase();
      if (q.length < 2) { results.classList.remove('open'); return; }

      const model = getModel();
      if (!model) return;

      const hits = [];
      for (const diag of model.diagrams) {
        for (const part of diag.parts) {
          if (
            part.name.toLowerCase().includes(q) ||
            part.partNumber.toLowerCase().includes(q) ||
            part.num.toLowerCase().includes(q)
          ) {
            hits.push({ diag, part });
            if (hits.length >= 20) break;
          }
        }
        if (hits.length >= 20) break;
      }

      if (!hits.length) {
        results.innerHTML = '<div class="pf-search-no-results">No parts found for "<em>' + input.value + '</em>"</div>';
      } else {
        results.innerHTML = hits.map(({ diag, part }) => `
          <div class="pf-search-result-item" onclick="pfSearchGoTo('${diag.code.replace(/'/g,"\\'")}')">
            <span class="pf-search-result-num">${part.num}</span>
            <div class="pf-search-result-info">
              <div class="pf-search-result-name">${part.name}</div>
              <div class="pf-search-result-meta">${diag.code} · ${diag.name} · ${fmtPrice(part.rrp)}</div>
            </div>
            <span class="pf-search-result-pn">${part.partNumber}</span>
          </div>`).join('');
      }
      results.classList.add('open');
    });

    document.addEventListener('click', e => {
      if (!$('#pf-global-search')?.contains(e.target)) {
        results.classList.remove('open');
      }
    });
  }

  window.pfSearchGoTo = function (diagramCode) {
    const results = $('#pf-global-search-results');
    if (results) results.classList.remove('open');
    pfSelectDiagram(diagramCode);
  };

  function clearGlobalSearch() {
    const input = $('#pf-global-search-input');
    const results = $('#pf-global-search-results');
    if (input) input.value = '';
    if (results) results.classList.remove('open');
  }

  // ── View: Brands ──────────────────────────
  const BRAND_LOGO_URLS = {
    cfmoto:    'https://cdn.shopify.com/s/files/1/0868/5548/3677/files/CFMoto_Parts_Accessories.webp',
    kymco:     'https://cdn.shopify.com/s/files/1/0868/5548/3677/files/Kymco_Parts_Accessories.webp?v=1785377738',
    lambretta: 'https://cdn.shopify.com/s/files/1/0868/5548/3677/files/Lambretta_Parts_Accessories.webp?v=1785377737',
  };

  function renderBrands() {
    const c = $('#pf-brands-grid');
    if (!c) return;
    c.innerHTML = DATA.brands.map(brand => {
      const total = (brand.models || []).reduce((s, m) =>
        s + (m.diagrams || []).reduce((ss, d) => ss + d.parts.length, 0), 0);
      const logoUrl = BRAND_LOGO_URLS[brand.id] || '';
      const modelCount = (brand.models||[]).length;
      return `
        <div class="pf-brand-card" onclick="pfSelectBrand('${brand.id}')">
          ${logoUrl
            ? `<img src="${logoUrl}" alt="${brand.name}" style="width:100%;max-width:200px;height:auto;object-fit:contain;margin-bottom:12px;display:block;margin-left:auto;margin-right:auto">`
            : `<div style="font-size:30px;font-weight:900;color:#e4002b;letter-spacing:-1px;margin-bottom:8px">${brand.name}</div>`
          }
          <h3 style="margin:0 0 4px">${brand.label || brand.name + ' Parts'}</h3>
          <p style="margin:0;font-size:13px;color:#6b7280">${modelCount} model${modelCount !== 1 ? 's' : ''} · ${total.toLocaleString()} parts</p>
        </div>`;
    }).join('');
  }

  window.pfSelectBrand = function (brandId) {
    state.brandId = brandId;
    renderModels();
    showView('models');
  };

  // ── View: Models (unique names) ───────────
  function renderModels() {
    const c = $('#pf-models-grid');
    if (!c) return;
    const brand = getBrand();
    if (!brand) return;

    // Group by unique model name
    const nameMap = {};
    for (const m of brand.models || []) {
      if (!nameMap[m.name]) nameMap[m.name] = [];
      nameMap[m.name].push(m);
    }

    // Build engine-size filter bar
    let filterEl = $('#pf-engine-filter');
    if (!filterEl) {
      filterEl = document.createElement('div');
      filterEl.id = 'pf-engine-filter';
      filterEl.className = 'pf-engine-filter';
      c.parentNode.insertBefore(filterEl, c);
    }
    const buckets = CONFIG.ccBuckets;
    const bucketLabels = {
      'ALL': 'All Models',
      '150-299': '150–299 cc',
      '300-599': '300–599 cc',
      '600-799': '600–799 cc',
      '800-1000': '800–1000 cc',
    };
    if (buckets.length === 0) {
      filterEl.innerHTML = '';
      filterEl.style.display = 'none';
    } else {
      filterEl.style.display = '';
      filterEl.innerHTML = buckets.map(b =>
        `<button class="pf-engine-btn${state.ccFilter === b ? ' active' : ''}" onclick="pfFilterCC('${b}')">${bucketLabels[b]}</button>`
      ).join('');
    }

    // Render cards — sorted numerically by CC prefix, then alphabetically
    const sortedModels = Object.entries(nameMap).sort(([a], [b]) => {
      const numA = parseInt((a.match(/\d+/) || ['0'])[0]);
      const numB = parseInt((b.match(/\d+/) || ['0'])[0]);
      if (numA !== numB) return numA - numB;
      return a.localeCompare(b);
    });
    c.innerHTML = sortedModels.map(([name, models]) => {
      const years      = models.map(m => m.year).sort();
      const totalParts = models.reduce((s, m) =>
        s + m.diagrams.reduce((ss, d) => ss + d.parts.length, 0), 0);
      // Image: prefer data.js CDN url, no fallback needed (all models have images now)
      const imgUrl  = models.find(m => m.image)?.image || '';
      const bucket  = getEngineBucket(name);
      const yearRange = years.length === 1 ? years[0] : `${years[0]}–${years[years.length - 1]}`;
      const hiddenStyle = (state.ccFilter !== 'ALL' && state.ccFilter !== bucket) ? 'display:none' : '';
      return `
        <div class="pf-model-card" data-cc-bucket="${bucket}" style="${hiddenStyle}" onclick="pfSelectModelName('${name.replace(/'/g,"\\'")}')">
          <div class="pf-model-card-img">
            ${imgUrl
              ? `<img src="${imgUrl}" alt="${name}" loading="lazy">`
              : `<svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" stroke-width="1"><rect x="2" y="7" width="20" height="10" rx="2"/><circle cx="6" cy="17" r="2"/><circle cx="18" cy="17" r="2"/></svg>`}
          </div>
          <div class="pf-model-card-body">
            <h3>${name}</h3>
            <p>${yearRange} · ${totalParts.toLocaleString()} parts</p>
          </div>
        </div>`;
    }).join('');
  }

  window.pfFilterCC = function (bucket) {
    state.ccFilter = bucket;
    // Update button states
    $$('.pf-engine-btn').forEach(btn => btn.classList.toggle('active', btn.textContent === ({
      'ALL': 'All Models', '150-299': '150–299 cc', '300-599': '300–599 cc',
      '600-799': '600–799 cc', '800-1000': '800–1000 cc',
    })[bucket]));
    // Show/hide cards
    $$('.pf-model-card').forEach(card => {
      const show = bucket === 'ALL' || card.dataset.ccBucket === bucket;
      card.style.display = show ? '' : 'none';
    });
  };

  window.pfSelectModelName = function (name) {
    state.modelName = name;
    const forName = getModelsForName();
    if (forName.length === 1) {
      // Only 1 year — skip year step
      state.modelId = forName[0].id;
      renderDiagrams();
      showView('diagrams');
    } else {
      renderYears();
      showView('years');
    }
  };

  // ── View: Years ───────────────────────────
  function renderYears() {
    const c = $('#pf-years-grid');
    if (!c) return;
    const models = getModelsForName().slice().sort((a, b) => a.year.localeCompare(b.year));
    c.innerHTML = models.map(m => {
      const partCount = m.diagrams.reduce((s, d) => s + d.parts.length, 0);
      return `
        <div class="pf-year-card" onclick="pfSelectModel('${m.id}')">
          <div class="pf-year-card-year">${m.year}</div>
          <div class="pf-year-card-info">
            <span>${m.diagrams.length} diagrams</span>
            <span>${partCount} parts</span>
          </div>
        </div>`;
    }).join('');
  }

  window.pfSelectModel = function (modelId) {
    state.modelId = modelId;
    state.sectionFilter = 'ALL';
    renderDiagrams();
    showView('diagrams');
  };

  // ── View: Diagrams ────────────────────────
  function renderDiagrams() {
    const model = getModel();
    if (!model) return;

    const heading = $('#pf-diagrams-heading');
    if (heading) heading.textContent = `${model.name} ${model.year} — ${model.diagrams.length} Diagrams`;

    const tabsEl = $('#pf-section-tabs');
    if (tabsEl) {
      const sections = ['ALL', ...new Set(model.diagrams.map(d => d.section))];
      tabsEl.innerHTML = sections.map(s =>
        `<button class="pf-section-tab${s === state.sectionFilter ? ' active' : ''}" onclick="pfFilterSection('${s}')">${s}</button>`
      ).join('');
    }
    renderDiagramCards();
  }

  window.pfFilterSection = function (section) {
    state.sectionFilter = section;
    $$('.pf-section-tab').forEach(t => t.classList.toggle('active', t.textContent === section));
    renderDiagramCards();
  };

  function renderDiagramCards() {
    const model = getModel();
    if (!model) return;
    const c = $('#pf-diagrams-grid');
    if (!c) return;
    const filtered = model.diagrams.filter(d =>
      state.sectionFilter === 'ALL' || d.section === state.sectionFilter
    );
    if (!filtered.length) { c.innerHTML = '<div class="pf-no-results">No diagrams found.</div>'; return; }

    c.innerHTML = filtered.map(diag => `
      <div class="pf-diagram-card" onclick="pfSelectDiagram('${diag.code.replace(/'/g, "\\'")}')">
        <div class="pf-diagram-card-img">
          ${diag.imageUrl
            ? `<img src="${diag.imageUrl}" alt="${diag.name}" loading="lazy">`
            : `<svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/></svg>`}
        </div>
        <div class="pf-diagram-card-body">
          <div class="pf-diagram-code">${diag.code}</div>
          <div class="pf-diagram-name">${diag.name}</div>
          <div class="pf-diagram-count">${diag.parts.length} parts</div>
        </div>
      </div>`).join('');
  }

  // ── View: Diagram Detail ──────────────────
  window.pfSelectDiagram = function (code) {
    state.diagramCode = code;
    state.searchQuery = '';
    _liveAvail = {};  // clear stale live data from previous diagram
    clearGlobalSearch();
    renderDetail();
    showView('detail');
    // Fetch live availability in background — updates UI in-place when ready
    const diag = getDiagram();
    if (diag) fetchLiveAvailability(diag).catch(() => {});
  };

  async function fetchLiveAvailability(diag) {
    const parts = diag.parts.filter(p => p.variantId);
    if (!parts.length) return;

    // Fetch all variants in parallel via public /variants/{id}.js endpoint
    const results = await Promise.allSettled(
      parts.map(p =>
        fetch(`/variants/${p.variantId}.js`)
          .then(r => r.ok ? r.json() : Promise.reject(r.status))
          .then(v => ({ variantId: p.variantId, available: !!v.available, price: v.price || 0 }))
      )
    );

    // Build live availability map
    for (const result of results) {
      if (result.status === 'fulfilled') {
        const { variantId, available, price } = result.value;
        _liveAvail[variantId] = { available, price };
      }
    }

    // Update DOM in-place for each part (no full re-render — preserves scroll & search)
    const model = getModel();
    const modelLabel = model ? `${model.name} ${model.year}` : '';
    const diagLabel  = esc(diag.code + ' — ' + diag.name);

    for (const part of diag.parts) {
      if (!part.variantId || !(part.variantId in _liveAvail)) continue;
      const rowId = 'pf-part-' + part.partNumber.replace(/[^a-z0-9]/gi, '-');
      const row = document.getElementById(rowId);
      if (!row) continue;  // row filtered out by search — skip

      const live      = _liveAvail[part.variantId];
      // Use _pfOOS stock map for accuracy (available from /variants/.js is always true with continue policy)
      const available = window._pfOOS
        ? !window._pfOOS.has(part.variantId)
        : (live.available && live.price > 0);
      const livePrice = live.price > 0 ? live.price / 100 : null;
      const displayPrice = livePrice || part.rrp;

      // Update price cell if live price differs
      if (livePrice && Math.abs(livePrice - part.rrp) > 0.005) {
        const priceEl = row.querySelector('.pf-td.price');
        if (priceEl) priceEl.innerHTML = `${fmtPrice(livePrice)}<small>inc. GST</small>`;
      }

      // Update stock indicator
      const stockCls  = available ? 'in-stock' : 'out-of-stock';
      const stockText = available ? 'In Stock'  : 'Out of Stock';
      const indicator = row.querySelector('.pf-stock-indicator');
      if (indicator) {
        indicator.innerHTML = `
          <span class="pf-stock-dot ${stockCls}"></span>
          <span class="pf-stock-label ${stockCls}">${stockText}</span>`;
      }

      // Replace action button
      const stockCell = row.querySelector('.pf-td.stock');
      const existingBtn = stockCell && stockCell.querySelector('button');
      if (existingBtn) {
        if (available) {
          existingBtn.className   = 'pf-btn-row-action order';
          existingBtn.disabled    = false;
          existingBtn.textContent = 'Add to Cart';
          existingBtn.setAttribute('data-variant-id', part.variantId);
          existingBtn.onclick = function () { pfAddToCart(part.variantId, this); };
        } else {
          // Out of stock — backorder button (gray Add to Cart)
          existingBtn.className   = 'pf-btn-row-action backorder';
          existingBtn.disabled    = false;
          existingBtn.textContent = 'Add to Cart';
          existingBtn.setAttribute('data-variant-id', part.variantId);
          existingBtn.onclick = function () { pfAddToCartBackorder(part.variantId, this, part.name); };
        }
      }
    }
  }

  function renderDetail() {
    const diag = getDiagram();
    if (!diag) return;
    const model = getModel();
    const detailEl = $('#pf-view-detail');
    if (!detailEl) return;

    pfZoomState = 1; panX = 0; panY = 0; isPanning = false;

    // Trust / info block — used in both desktop (inside left panel) and mobile (after parts)
    const infoHtml = `
      <div class="pf-diagram-hint">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
        Use Zoom In to read part numbers on the diagram
      </div>
      <div class="pf-trust-badges">
        <div class="pf-trust-badge">
          <div class="pf-trust-badge-icon">🔒</div>
          <div class="pf-trust-badge-text">
            <div class="pf-trust-badge-title">OEM Parts Only</div>
            <div class="pf-trust-badge-desc">Genuine manufacturer parts, no aftermarket substitutions</div>
          </div>
        </div>
        <div class="pf-trust-badge">
          <div class="pf-trust-badge-icon">🚚</div>
          <div class="pf-trust-badge-text">
            <div class="pf-trust-badge-title">Flat Rate Shipping</div>
            <div class="pf-trust-badge-desc">$19 Australia-wide or free Click &amp; Collect in Alexandria</div>
          </div>
        </div>
        <div class="pf-trust-badge">
          <div class="pf-trust-badge-icon">📦</div>
          <div class="pf-trust-badge-text">
            <div class="pf-trust-badge-title">Shipping Time Frame</div>
            <div class="pf-trust-badge-desc">Available parts 2–4 days &middot; Backorders 4–6 weeks</div>
          </div>
        </div>
        <div class="pf-trust-badge">
          <div class="pf-trust-badge-icon">🛠️</div>
          <div class="pf-trust-badge-text">
            <div class="pf-trust-badge-title">CFMoto Certified Workshop</div>
            <div class="pf-trust-badge-desc">Authorised dealer in Alexandria, Sydney — professional fitting services available</div>
          </div>
        </div>
      </div>`;

    detailEl.innerHTML = `
      <div class="pf-detail-layout">

        <!-- Left: sticky panel (image + meta) -->
        <div class="pf-diagram-left-panel">
          <div class="pf-diagram-image-wrap">
            <div class="pf-img-container">
              <img id="pf-detail-image" src="${diag.imageUrl || ''}" alt="${esc(diag.name)}">
            </div>
            <div class="pf-zoom-controls">
              <button class="pf-zoom-btn" onclick="pfZoom(-0.25)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                Zoom Out
              </button>
              <button class="pf-zoom-btn" onclick="pfZoom(0.25)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                Zoom In
              </button>
              <button class="pf-zoom-btn reset" onclick="pfZoomReset()">Reset</button>
              <span class="pf-zoom-level" id="pf-zoom-level">100%</span>
            </div>
          </div>
          <div class="pf-diagram-meta">
            <div class="pf-meta-item">
              <div class="pf-meta-label">Model</div>
              <div class="pf-meta-value">${model ? model.name : '—'}</div>
            </div>
            <div class="pf-meta-item">
              <div class="pf-meta-label">Year</div>
              <div class="pf-meta-value">${model ? model.year : '—'}</div>
            </div>
            <div class="pf-meta-item pf-meta-desktop-only">
              <div class="pf-meta-label">Diagram</div>
              <div class="pf-meta-value">${diag.code}</div>
            </div>
            <div class="pf-meta-item pf-meta-desktop-only">
              <div class="pf-meta-label">Section</div>
              <div class="pf-meta-value">${diag.section || '—'}</div>
            </div>
          </div>
          <!-- Desktop only: info below image -->
          <div class="pf-info-desktop">${infoHtml}</div>
        </div>

        <!-- Right: parts table -->
        <div class="pf-parts-panel">
          <div class="pf-parts-search-wrap">
            <div class="pf-parts-search-icon">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            </div>
            <input id="pf-parts-search" type="text" placeholder="Filter parts in this diagram…">
          </div>
          <div class="pf-parts-table-wrap">
            <div class="pf-parts-table-header">
              <div class="pf-th">#</div>
              <div class="pf-th">Part Name</div>
              <div class="pf-th sku-col">SKU</div>
              <div class="pf-th">Price</div>
              <div class="pf-th">Stock / Action</div>
            </div>
            <div class="pf-parts-count-bar" id="pf-parts-count"></div>
            <div class="pf-parts-list" id="pf-parts-list"></div>
          </div>
        </div>

        <!-- Mobile only: info after parts table -->
        <div class="pf-info-mobile">${infoHtml}</div>

      </div>`;

    const searchInput = $('#pf-parts-search');
    if (searchInput) {
      searchInput.value = '';
      searchInput.oninput = e => { state.searchQuery = e.target.value.toLowerCase(); renderParts(); };
    }
    renderParts();
    initImagePanContainer();
  }

  function renderParts() {
    const diag = getDiagram();
    if (!diag) return;
    const c = $('#pf-parts-list');
    const countEl = $('#pf-parts-count');
    if (!c) return;

    const q = state.searchQuery;
    const filtered = q
      ? diag.parts.filter(p =>
          p.name.toLowerCase().includes(q) ||
          p.partNumber.toLowerCase().includes(q) ||
          p.num.toLowerCase().includes(q))
      : diag.parts;

    if (countEl) countEl.innerHTML = `Showing <strong>${filtered.length}</strong> of <strong>${diag.parts.length}</strong> parts`;

    if (!filtered.length) { c.innerHTML = '<div class="pf-no-results">No parts match your search.</div>'; return; }

    const model = getModel();
    const modelLabel = model ? `${model.name} ${model.year}` : '';
    const diagLabel = esc(diag.code + ' — ' + diag.name);

    c.innerHTML = filtered.map(part => {
      const inStock  = isInStock(part);
      const numClean = part.num.replace('#', '');
      const stockCls  = inStock ? 'in-stock'  : 'out-of-stock';
      const stockText = inStock ? 'In Stock'   : 'Out of Stock';

      // Use live price if already loaded, else fall back to static RRP
      const liveP        = getLivePrice(part);
      const displayPrice = liveP || part.rrp;

      let btnHtml;
      if (inStock && part.variantId) {
        btnHtml = `<button class="pf-btn-row-action order" data-variant-id="${part.variantId}"
             onclick="pfAddToCart(${part.variantId}, this)">
             Add to Cart
           </button>`;
      } else if (!inStock && part.variantId) {
        // Out of stock but has a Shopify product — allow backorder with warning
        btnHtml = `<button class="pf-btn-row-action backorder" data-variant-id="${part.variantId}"
             onclick="pfAddToCartBackorder(${part.variantId}, this, '${esc(part.name)}')">
             Add to Cart
           </button>`;
      } else if (inStock) {
        btnHtml = `<button class="pf-btn-row-action order"
             onclick="pfOrderNow('${esc(part.partNumber)}','${esc(part.name)}','${fmtPrice(displayPrice)}','${esc(modelLabel)}','${diagLabel}',true)">
             Order Now
           </button>`;
      } else {
        btnHtml = `<button class="pf-btn-row-action enquire"
             onclick="pfOrderNow('${esc(part.partNumber)}','${esc(part.name)}','${fmtPrice(displayPrice)}','${esc(modelLabel)}','${diagLabel}',false)">
             Enquire
           </button>`;
      }

      return `
        <div class="pf-part-row" id="pf-part-${part.partNumber.replace(/[^a-z0-9]/gi, '-')}">
          <div class="pf-td"><span class="pf-part-num-badge">${part.num}</span></div>
          <div class="pf-td pf-part-name-cell">
            <div class="pf-part-name-main">${part.name}</div>
            ${part.qty > 1 ? `<div class="pf-part-qty-note">Qty in assembly: ${part.qty}</div>` : ''}
          </div>
          <div class="pf-td sku">${part.partNumber}</div>
          <div class="pf-td price">${fmtPrice(displayPrice)}<small>inc. GST</small></div>
          <div class="pf-td stock">
            <div class="pf-stock-indicator">
              <span class="pf-stock-dot ${stockCls}"></span>
              <span class="pf-stock-label ${stockCls}">${stockText}</span>
            </div>
            ${btnHtml}
          </div>
        </div>`;
    }).join('');
  }

  function esc(s) { return String(s).replace(/'/g, "\\'").replace(/"/g, '&quot;'); }

  // ── Order / Enquiry Modal ─────────────────
  window.pfOrderNow = function (partNumber, partName, price, modelLabel, diagLabel, inStock) {
    const modal = $('#pf-modal-backdrop');
    if (!modal) return;

    $('#pf-modal-title').textContent = inStock ? 'Order This Part' : 'Enquire About This Part';
    $('#pf-modal-subtitle').textContent = inStock
      ? 'Complete the form below and we\'ll send you an invoice to confirm your order.'
      : 'This part is currently on order. Submit your enquiry and we\'ll get back to you.';

    $('#pf-modal-part-number').textContent = partNumber;
    $('#pf-modal-part-name').textContent = partName;
    $('#pf-modal-part-price').textContent = price;
    $('#pf-modal-part-diagram').textContent = diagLabel;

    const shippingNote = $('#pf-modal-shipping-note');
    if (shippingNote) shippingNote.style.display = inStock ? 'block' : 'none';

    const submitBtn = $('#pf-modal-submit');
    if (submitBtn) submitBtn.textContent = inStock ? 'Submit Order Request' : 'Send Enquiry';

    const hiddenBody = $('#pf-enquiry-body');
    if (hiddenBody) {
      hiddenBody.value = [
        inStock ? 'ORDER REQUEST' : 'PARTS ENQUIRY',
        '---',
        `Part: ${partNumber} — ${partName}`,
        `Price: ${price} inc. GST`,
        `Model: ${modelLabel}`,
        `Diagram: ${diagLabel}`,
        inStock ? 'Status: In Stock — customer wants to order' : 'Status: Out of Stock — customer enquiry'
      ].join('\n');
    }

    modal.classList.add('open');
    document.body.style.overflow = 'hidden';
  };

  window.pfCloseModal = function () {
    const modal = $('#pf-modal-backdrop');
    if (modal) modal.classList.remove('open');
    document.body.style.overflow = '';
  };

  // ── Add to Cart (AJAX) ────────────────────
  window.pfAddToCart = function (variantId, btnEl) {
    btnEl.disabled = true;
    const orig = btnEl.textContent;
    btnEl.textContent = '...';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
    .then(r => r.json())
    .then(data => {
      if (data.items || data.item) {
        btnEl.classList.add('added');
        btnEl.textContent = '✓ Added!';
        fetch('/cart.js')
          .then(r => r.json())
          .then(cart => {
            document.querySelectorAll('[data-cart-item-count], .cart-count-bubble span, .cart-count').forEach(el => {
              el.textContent = cart.item_count;
            });
          });
        setTimeout(() => {
          btnEl.disabled = false;
          btnEl.textContent = orig;
          btnEl.classList.remove('added');
        }, 2500);
      } else {
        btnEl.disabled = false;
        btnEl.textContent = orig;
        alert(data.description || 'Could not add to cart. Please try again.');
      }
    })
    .catch(() => {
      btnEl.disabled = false;
      btnEl.textContent = orig;
    });
  };

  // ── Backorder warning modal ───────────────
  window.pfAddToCartBackorder = function (variantId, btnEl, partName) {
    let modal = document.getElementById('pf-backorder-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.id = 'pf-backorder-modal';
      modal.style.cssText = [
        'position:fixed','inset:0','background:rgba(0,0,0,0.55)',
        'z-index:99999','display:flex','align-items:center',
        'justify-content:center','padding:20px',
      ].join(';');
      document.body.appendChild(modal);
      modal.addEventListener('click', e => {
        if (e.target === modal) pfCloseBackorderModal();
      });
    }

    modal.innerHTML = `
      <div style="background:#fff;border-radius:14px;max-width:460px;width:100%;
                  padding:28px 28px 24px;box-shadow:0 24px 64px rgba(0,0,0,0.25);font-family:inherit">

        <!-- Header -->
        <div style="display:flex;align-items:flex-start;gap:14px;margin-bottom:18px">
          <div style="width:42px;height:42px;min-width:42px;background:#fff7ed;border-radius:10px;
                      display:flex;align-items:center;justify-content:center">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f97316" stroke-width="2.2">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
          </div>
          <div>
            <div style="font-size:15px;font-weight:800;color:#111;margin-bottom:3px">Backordered Part</div>
            <div style="font-size:12.5px;color:#6b7280">This item is not currently in stock</div>
          </div>
          <button onclick="pfCloseBackorderModal()"
                  style="margin-left:auto;background:none;border:none;cursor:pointer;color:#9ca3af;
                         font-size:20px;line-height:1;padding:0;flex-shrink:0">✕</button>
        </div>

        <!-- Warning items -->
        <div style="background:#f9fafb;border-radius:10px;padding:16px 18px;margin-bottom:18px">
          <div style="font-size:12px;font-weight:700;color:#374151;margin-bottom:10px;text-transform:uppercase;letter-spacing:.5px">
            Please read before ordering
          </div>
          <ul style="margin:0;padding-left:18px;font-size:13px;color:#374151;line-height:2.1">
            <li><strong>Estimated Lead Time: 4–6 Weeks</strong> from date of order</li>
            <li>Some items may be <strong>Special Order only</strong></li>
            <li>This item is <strong>non-cancellable</strong> once placed</li>
            <li>This item is <strong>non-returnable</strong></li>
          </ul>
        </div>

        <!-- Contact note -->
        <div style="font-size:12.5px;color:#555;margin-bottom:22px;
                    border-left:3px solid #e4002b;padding-left:12px;line-height:1.6">
          If you have any questions before ordering, please call us first:<br>
          <strong style="font-size:14.5px;color:#111">02 8591 1062</strong>
        </div>

        <!-- Buttons -->
        <div style="display:flex;gap:10px">
          <button onclick="pfCloseBackorderModal()"
                  style="flex:1;padding:11px 16px;border:1.5px solid #d1d5db;border-radius:8px;
                         background:#fff;font-size:13px;font-weight:700;cursor:pointer;color:#374151">
            Cancel
          </button>
          <button id="pf-backorder-confirm"
                  style="flex:2;padding:11px 16px;border:none;border-radius:8px;
                         background:#111;color:#fff;font-size:13px;font-weight:700;cursor:pointer">
            Add to Cart — I Understand
          </button>
        </div>
      </div>`;

    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    document.getElementById('pf-backorder-confirm').onclick = function () {
      pfCloseBackorderModal();
      pfAddToCart(variantId, btnEl);
    };
  };

  window.pfCloseBackorderModal = function () {
    const modal = document.getElementById('pf-backorder-modal');
    if (modal) modal.style.display = 'none';
    document.body.style.overflow = '';
  };

  // ── Zoom + Pan ────────────────────────────
  function updateImageTransform() {
    const img = $('#pf-detail-image');
    if (!img) return;
    if (pfZoomState === 1 && panX === 0 && panY === 0) {
      img.style.transform = '';
    } else {
      img.style.transform = `scale(${pfZoomState}) translate(${panX / pfZoomState}px, ${panY / pfZoomState}px)`;
    }
  }

  function initImagePanContainer() {
    const container = document.querySelector('#pf-view-detail .pf-img-container');
    if (!container) return;
    container.style.cursor = 'default';

    container.addEventListener('mousedown', e => {
      if (pfZoomState <= 1) return;
      isPanning = true;
      panStartX = e.clientX - panX;
      panStartY = e.clientY - panY;
      container.style.cursor = 'grabbing';
      e.preventDefault();
    });

    container.addEventListener('touchstart', e => {
      if (pfZoomState <= 1) return;
      isPanning = true;
      const t = e.touches[0];
      panStartX = t.clientX - panX;
      panStartY = t.clientY - panY;
      e.preventDefault();
    }, { passive: false });
  }

  function initGlobalPanListeners() {
    document.addEventListener('mousemove', e => {
      if (!isPanning) return;
      panX = e.clientX - panStartX;
      panY = e.clientY - panStartY;
      updateImageTransform();
    });
    document.addEventListener('mouseup', () => {
      if (!isPanning) return;
      isPanning = false;
      const c = document.querySelector('#pf-view-detail .pf-img-container');
      if (c) c.style.cursor = pfZoomState > 1 ? 'grab' : 'default';
    });
    document.addEventListener('touchmove', e => {
      if (!isPanning) return;
      const t = e.touches[0];
      panX = t.clientX - panStartX;
      panY = t.clientY - panStartY;
      updateImageTransform();
    }, { passive: false });
    document.addEventListener('touchend', () => { isPanning = false; });
  }

  function enableStickyPanel() {
    const detail = document.getElementById('pf-view-detail');
    const panel = detail && detail.querySelector('.pf-diagram-left-panel');
    if (!panel) return;

    if (window._pfStickyOff) { window._pfStickyOff(); window._pfStickyOff = null; }
    panel.style.transform = '';

    let headerH = 0;
    document.querySelectorAll('sticky-header, header, .header-wrapper, .site-header').forEach(el => {
      const pos = window.getComputedStyle(el).position;
      if (pos === 'sticky' || pos === 'fixed') {
        headerH = Math.max(headerH, el.getBoundingClientRect().bottom);
      }
    });
    const gap = Math.max(headerH + 20, 100);

    function tick() {
      if (window.innerWidth < 960) { panel.style.transform = ''; return; }
      const layout = panel.parentElement;
      if (!layout) return;
      const layoutTop = layout.getBoundingClientRect().top;
      const pushed = Math.max(0, gap - layoutTop);
      const maxPush = Math.max(0, layout.offsetHeight - panel.offsetHeight - 20);
      const y = Math.min(pushed, maxPush);
      panel.style.transform = y > 0 ? `translateY(${y}px)` : '';
    }

    window.addEventListener('scroll', tick, { passive: true });
    window.addEventListener('resize', tick, { passive: true });
    window._pfStickyOff = () => {
      window.removeEventListener('scroll', tick);
      window.removeEventListener('resize', tick);
      if (panel) panel.style.transform = '';
    };
    tick();
  }

  window.pfZoom = function (delta) {
    pfZoomState = Math.min(4, Math.max(0.5, pfZoomState + delta));
    const container = document.querySelector('#pf-view-detail .pf-img-container');
    if (container) container.style.cursor = pfZoomState > 1 ? 'grab' : 'default';
    updateImageTransform();
    const lvl = $('#pf-zoom-level');
    if (lvl) lvl.textContent = Math.round(pfZoomState * 100) + '%';
  };

  window.pfZoomReset = function () {
    pfZoomState = 1; panX = 0; panY = 0;
    const container = document.querySelector('#pf-view-detail .pf-img-container');
    if (container) container.style.cursor = 'default';
    updateImageTransform();
    const lvl = $('#pf-zoom-level');
    if (lvl) lvl.textContent = '100%';
  };

  document.addEventListener('click', e => {
    const modal = $('#pf-modal-backdrop');
    if (modal && e.target === modal) pfCloseModal();
  });

  // ── Init ──────────────────────────────────
  function _restoreDeepLink(deep) {
    // Returns true if a valid deep-link state was restored
    const brd = DATA.brands.find(b => b.id === deep.brand);
    if (!brd) return false;
    state.brandId = brd.id;

    if (!deep.model) {
      // Just brand level → models view
      _isRestoringHistory = true;
      renderModels();
      showView('models');
      _isRestoringHistory = false;
      return true;
    }

    const yearModels = brd.models.filter(m => m.name === deep.model);
    if (!yearModels.length) return false;
    state.modelName = deep.model;

    if (!deep.year) {
      // Model selected, no year → years view
      _isRestoringHistory = true;
      renderYears();
      showView('years');
      _isRestoringHistory = false;
      return true;
    }

    const m = yearModels.find(m => String(m.year) === deep.year) || yearModels[0];
    if (!m) return false;
    state.modelId = m.id;

    if (!deep.diag) {
      // Year selected → diagrams view
      _isRestoringHistory = true;
      renderDiagrams();
      showView('diagrams');
      _isRestoringHistory = false;
      return true;
    }

    const diag = m.diagrams.find(d => d.code === deep.diag);
    if (!diag) {
      // Diag code not found → fall back to diagrams view
      _isRestoringHistory = true;
      renderDiagrams();
      showView('diagrams');
      _isRestoringHistory = false;
      return true;
    }

    state.diagramCode = diag.code;
    _isRestoringHistory = true;
    renderDetail();
    showView('detail');
    _isRestoringHistory = false;
    fetchLiveAvailability(diag).catch(() => {});
    return true;
  }

  function init() {
    if (!DATA.brands.length) return;
    initGlobalSearch();
    initGlobalPanListeners();

    // Brand-specific page: auto-select brand, then try to restore from ?pf= URL param
    if (CONFIG.brand) {
      state.brandId = CONFIG.brand;
      const deep = _parsePfUrl();
      if (deep && deep.model && _restoreDeepLink(deep)) {
        // Restored from deep link — lock in the history entry without adding a new one
        const hs = { view: state.view, brandId: state.brandId, modelName: state.modelName,
          modelId: state.modelId, diagramCode: state.diagramCode, sectionFilter: 'ALL', ccFilter: 'ALL' };
        history.replaceState(hs, '', _buildPfUrl(hs));
        return;
      }
      const initState = { view: 'models', brandId: CONFIG.brand, modelName: null, modelId: null,
        diagramCode: null, sectionFilter: 'ALL', ccFilter: 'ALL' };
      history.replaceState(initState, '', location.pathname);
      renderModels();
      showView('models');
      return;
    }

    // Home page: try to restore from ?pf= URL param
    const deep = _parsePfUrl();
    if (deep && deep.brand && _restoreDeepLink(deep)) {
      const hs = { view: state.view, brandId: state.brandId, modelName: state.modelName,
        modelId: state.modelId, diagramCode: state.diagramCode, sectionFilter: 'ALL', ccFilter: 'ALL' };
      history.replaceState(hs, '', _buildPfUrl(hs));
      return;
    }

    renderBrands();
    // Register the initial state in browser history (replaceState so we don't add an extra entry)
    history.replaceState(
      { view: 'brands', brandId: null, modelName: null, modelId: null,
        diagramCode: null, sectionFilter: 'ALL', ccFilter: 'ALL' },
      '',
      location.pathname
    );

    if (DATA.brands.length === 1) {
      state.brandId = DATA.brands[0].id;
      const brand = getBrand();
      const uniqueNames = [...new Set((brand.models || []).map(m => m.name))];

      if (uniqueNames.length === 1) {
        state.modelName = uniqueNames[0];
        const forName = getModelsForName();
        if (forName.length === 1) {
          state.modelId = forName[0].id;
          renderDiagrams();
          showView('diagrams');
        } else {
          renderYears();
          showView('years');
        }
      } else {
        renderModels();
        showView('models');
      }
    } else {
      showView('brands');
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

})();
